import { RealtimeClient } from "./RealtimeClient";
import { RestClient } from "./restClient";

export class AttachmentService {
	constructor(options) {
		this._options = options;
		this._rc = new RealtimeClient();
		this._restClient = new RestClient();
	}

	/**
	 * Subscribe to a changefeed of files via a target's id
	 * @param {string} targetId -- Entity's id
	 * @param {function} callback
	 */
	subscribeByTarget(targetId, callback) {
		const args = {
			targetId: targetId,
			filterType: "by-target"
		};
		return this._rc.subscribe("ecosystem", "/attachments", args, function (msg) {
			const response = JSON.parse(msg);
			if (response) {
				if (callback) {
					callback(null, response);
				}
			}
		});
	}

	// attachFiles
	/**
	 * Upload a file, or upload a file and attach it to an entity/list/etc
	 * @param {string || null} targetId -- optional string, target's id for attaching a file to an entity/list/etc
	 * @param {array} files -- Array of files
	 * @param {function} callback
	 */
	uploadFiles(targetId, targetType, files, callback) {
		const formData = new FormData();
		for (let x = 0; x < files.length; x++) {
			formData.append("upload", files[x]);
		}
		formData.append("targetId", targetId);
		formData.append("targetType", targetType);
		formData.append("notify", true);
		const url = "/_upload";

		// If gets passed a callback, executes normally. Otherwise returns promise for use with async/await, etc.
		if (callback) {
			this._restClient.exec_multipartPost(url, formData, callback);
		} else {
			return new Promise((resolve, reject) => {
				this._restClient.exec_multipartPost(url, formData, (err, result) => {
					if (err) {
						reject(err);
						return;
					}
					resolve(result);
				});
			});
		}
	}

	// attachFilesWithoutNotification
	/**
	 * Upload a file without generating a notification, or upload a file and attach it to an entity/list/etc with no notification
	 * @param {string || null} targetId -- optional string, target's id for attaching a file to an entity/list/etc
	 * @param {array} files -- Array of files
	 * @param {function} callback
	 */
	uploadFilesWithoutNotification(targetId, files, callback) {
		const formData = new FormData();
		for (let x = 0; x < files.length; x++) {
			formData.append("upload", files[x]);
		}
		formData.append("targetId", targetId);
		formData.append("notify", false);
		const url = "/_upload";

		// If gets passed a callback, executes normally. Otherwise returns promise for use with async/await, etc.
		if (callback) {
			this._restClient.exec_multipartPost(url, formData, callback);
		} else {
			return new Promise((resolve, reject) => {
				this._restClient.exec_multipartPost(url, formData, (err, result) => {
					if (err) {
						reject(err);
						return;
					}
					resolve(result);
				});
			});
		}
	}

	/**
	 * Remove a file from a target, and delete it if it is no longer attached to anything
	 * @param {string} entityId -- Id of target to remove attachment from
	 * @param {string} fileHandle -- Handle of file to be removed
	 * @param {function} callback
	 */
	removeAttachment(entityId, entityType, fileId, callback) {
		// cSpell:ignore unattach
		const url = `/_unattach?fileId=${fileId}&entityId=${entityId}&entityType=${entityType}`;
		if (callback) {
			this._restClient.exec_delete(url, callback);
		}
	}

	/**
	 * Delete a file by its handle, regardless of how many things it is attached to.
	 * Will remove all entries related to file from sys_attachment and sys_entityAttachment
	 * @param {string} fileHandle -- Handle of the file you'd like to remove
	 * @param {function} callback
	 */
	deleteFiles(fileHandle, callback) {
		const url = `/_delete?handle=${fileHandle}`;
		if (callback) {
			this._restClient.exec_delete(url, callback);
		} else {
			return new Promise((resolve, reject) => {
				this._restClient.exec_delete(url, (err, result) => {
					if (err) {
						reject(err);
						return;
					} else {
						resolve(result);
					}
				});
			});
		}
	}

	/**
	 * Update a file from a target
	 * @param {string} fileId -- Id of file to be updated
	 * @param {string} targetId -- Id of the target the file is attached to
	 */
	updateFile(fileId, targetId) {
		const url = `/ecosystem/api/attachments/update/${fileId}/${targetId}`;
		this._restClient.exec_put(url);
	}
}
